import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Boxes, FileText, BarChart3, Settings, PlusCircle, LogOut, Menu, X, Printer, ClipboardList,
} from "lucide-react";
import { useEffect, useState } from "react";
import { GESTION, SAISIE, ALL, ROLE_LABELS, homeFor } from "@/lib/roles";
import InstallBanner from "@/components/InstallBanner";
import { startAutoSync } from "@/lib/offlineQueue";
import { toast } from "sonner";

const links = [
  { name: "Tableau de Bord", path: "/dashboard", icon: LayoutDashboard, id: "nav-dashboard-link", roles: GESTION },
  { name: "Nouvelle Palette", path: "/nouvelle-palette", icon: PlusCircle, id: "nav-nouvelle-palette-link", roles: ["operateur"] },
  { name: "Production du jour", path: "/production", icon: ClipboardList, id: "nav-production-link", roles: SAISIE },
  { name: "Palettes", path: "/palettes", icon: Boxes, id: "nav-palettes-link", roles: GESTION },
  { name: "Bons de Livraison", path: "/bl", icon: FileText, id: "nav-bl-link", roles: ALL },
  { name: "Bilan Mensuel", path: "/bilan", icon: BarChart3, id: "nav-bilan-link", roles: GESTION },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    startAutoSync((r) => toast.success(`${r.done} palette(s) hors-ligne synchronisée(s)`));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavItems = ({ onClick }) => (
    <>
      {links.filter((l) => l.roles.includes(user?.role)).map((l) => (
        <NavLink
          key={l.path}
          to={l.path}
          data-testid={l.id}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-2 px-4 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-colors ${
              isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`
          }
        >
          <l.icon className="w-5 h-5" /> {l.name}
        </NavLink>
      ))}
      {user?.role === "admin" && (
        <NavLink
          to="/admin"
          data-testid="nav-admin-link"
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-2 px-4 py-3 rounded-lg font-semibold text-sm uppercase tracking-wide transition-colors ${
              isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`
          }
        >
          <Settings className="w-5 h-5" /> Admin
        </NavLink>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white border-b-2 border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <NavLink to={homeFor(user?.role)} data-testid="nav-brand-link" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <span className="font-heading font-black text-amber-400 text-xl">A</span>
            </div>
            <div className="leading-none">
              <div className="font-heading font-black text-lg uppercase tracking-tight text-slate-900">
                Ferme d'Autheuil
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                27490 Autheuil-Authouillet
              </div>
            </div>
          </NavLink>

          <nav className="hidden lg:flex items-center gap-1">
            <NavItems />
          </nav>

          <div className="flex items-center gap-2">
            {SAISIE.includes(user?.role) && (
              <Button
                data-testid="nav-new-palette-btn"
                onClick={() => navigate("/nouvelle-palette")}
                className="hidden sm:flex bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 rounded-lg"
              >
                <PlusCircle className="w-5 h-5 mr-1" /> Nouvelle Palette
              </Button>
            )}
            <div className="hidden md:block text-right leading-none mr-1">
              <div className="text-sm font-bold text-slate-800 whitespace-nowrap">{user?.name}</div>
              <div data-testid="nav-user-role" className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-slate-500 hover:text-rose-600"
            >
              <LogOut className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-btn"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-200 p-3 flex flex-col gap-1 bg-white">
            <NavItems onClick={() => setOpen(false)} />
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <InstallBanner />
        <Outlet />
      </main>

      <footer className="no-print max-w-7xl mx-auto px-4 py-6 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400 font-semibold">
        <span>Ferme d'Autheuil — Traçabilité œufs</span>
        <NavLink to="/aide/imprimante" data-testid="footer-help-printer-link" className="flex items-center gap-1 hover:text-slate-700">
          <Printer className="w-4 h-4" /> Configuration imprimante
        </NavLink>
      </footer>
    </div>
  );
}
